// T008 — useAdminWallet: fetches wallet state + first page of transactions for an account,
// plus the cursor for the next page (null when the first page is all there is).
import { useCallback, useEffect, useState } from "react";

import { adminGetWallet } from "../services/wallet/adminWalletApi";
import type { WalletState, WalletTransaction } from "../types/wallet";

// `no_wallet` — 404 `wallet_not_found`: the account has never received credit.
// The API deliberately returns no zero state for admins; issuing creates it.
export type WalletFetchError = "no_wallet" | "not_found" | "forbidden" | "unknown";

interface UseAdminWalletResult {
  wallet: WalletState | null;
  transactions: WalletTransaction[];
  nextCursor: string | null;
  loading: boolean;
  error: WalletFetchError | null;
  refresh: () => Promise<void>;
  applyWalletState: (next: WalletState) => void;
}

const useAdminWallet = (accountId: string | undefined): UseAdminWalletResult => {
  const [wallet, setWallet] = useState<WalletState | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<WalletFetchError | null>(null);

  const refresh = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await adminGetWallet(accountId);
      setWallet(response.wallet);
      setTransactions(response.transactions ?? []);
      setNextCursor(response.next_cursor ?? null);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404 && err?.response?.data?.code === "wallet_not_found") {
        setError("no_wallet");
      } else if (status === 404) {
        setError("not_found");
      } else if (status === 403) {
        setError("forbidden");
      } else {
        console.error("Failed to load admin wallet", err);
        setError("unknown");
      }
      setWallet(null);
      setTransactions([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyWalletState = useCallback((next: WalletState) => {
    setWallet(next);
  }, []);

  return { wallet, transactions, nextCursor, loading, error, refresh, applyWalletState };
};

export default useAdminWallet;
